/* Live pane of today's book, grouped by appointment and broken out by service.
 * Rendered only while a `scheduling` plugin is switched on, and fed entirely by
 * the normalised rows that plugin returns — nothing here knows which system they
 * came from.
 *
 * Read-only: nothing here writes back to the outside system. Clicking a service
 * creates a turn on our own board and links to it by id. */
import * as Vue from 'vue';
import { store } from '../store.js';
import { ApptConflicts } from './appt-conflicts.js';
import { apptDrag } from './appt-drag.js';
import { StationSignIn } from './station-signin.js';

export const AppointmentsPane = {
  name: 'AppointmentsPane',
  components: {
    StationSignIn: StationSignIn,
    ApptConflicts: ApptConflicts
  },
  setup: function () {
    /* The book as the pane is showing it: every appointment, with the services
     * filtered to the ones still to be handed out when that switch is on. An
     * appointment with nothing left on it drops out entirely rather than
     * sitting there as a heading with no lines under it.
     *
     * Cancelled visits are counted as they are dropped, not merely skipped.
     * Hiding something silently is how a list starts lying about how much of
     * the day it is showing — the count under the switches is what makes the
     * hidden ones findable, so it has to be the number of headings the switch
     * would actually put back, after the other filter has had its say.
     */
    var filtered = Vue.computed(function () {
      var only = store.ui.apptUnassignedOnly;
      var hide = store.ui.apptHideCancelled;
      var rows = [];
      var cancelled = 0;
      store.appts.rows.forEach(function (a) {
        var services = a.services;
        if (only) {
          // Not work to hand out: a line with its own square, a line on a visit
          // that has checked out or that the board has already dealt with, and
          // a service added at the chair that is part of a square already up.
          services = services.filter(function (s) {
            return !store.turnForApptService(s.id) && !store.bookedLineOver(a, s) &&
              !store.addOnHostTurn(a, s);
          });
          if (!services.length) return;
        }
        if (a.cancelled && hide) { cancelled += 1; return; }
        rows.push({ appt: a, services: services });
      });
      return { rows: rows, cancelled: cancelled };
    });

    var visible = Vue.computed(function () { return filtered.value.rows; });
    var hiddenCancelled = Vue.computed(function () { return filtered.value.cancelled; });

    /* Each booked service, worked out once.
     *
     * The template used to ask turnState() eleven times and
     * trackerServiceForBooked() five times for a single line — every one of
     * them re-reading the turn index, re-walking the booking's options and,
     * in turnState's case, doing its own linear scan of the roster. All of it
     * for one row of text.
     *
     * The state on the line is the SERVICE's own, never the visit's. A visit
     * with a manicure at ten and a pedicure at half past is two pieces of
     * work, quite possibly with two technicians and certainly with two
     * squares — so "under way" is true of one of them and false of the other,
     * and there is no answer to give for the visit as a whole. Where each
     * line sits on its own clock is what the line says.
     */
    function decorate(row, now) {
      var a = row.appt;
      return {
        appt: a,
        services: row.services.map(function (s) {
          var mapped = store.trackerServiceForBooked(s);
          var turn = store.turnForApptService(s.id);
          var done = !!(turn && turn.endedAt != null);
          var tech = turn
            ? (turn.staffName || (store.staffOf(turn.staffId) || {}).name || '')
            : '';
          var requested = store.isApptRequested(s);
          // Mangomint's own request: shown, but not the desk's to take off.
          var fromBook = requested && store.isBookedRequest(s);
          // Whoever the book has on it, as a roster name. A booking nobody is
          // named on cannot be a request for anybody, so it gets no star —
          // the same rule the right-click menu and the hover card go by.
          var booked = store.bookedStaffFor(s);
          var at = store.formatClock(s.startsAt);

          // Only asked of a line with no square yet: once there is one, the
          // square is the record of that service and its own clock is the
          // one running. `late` is the line the desk has to do something
          // about — its time has been and gone and nobody has seated it.
          var when = '', state = 'later';
          // Covered without a square of its own: added at the chair onto a
          // square already up, or on a visit checked out / already dealt with.
          var host = !turn ? store.addOnHostTurn(a, s) : null;
          var over = !turn && !host && store.bookedLineOver(a, s);
          if (turn) {
            state = done ? 'done' : 'live';
          } else if (host || over) {
            state = 'done';
          } else if (store.bookedServiceEnd(s, mapped) < now) {
            state = 'late'; when = 'Late';
          } else if (s.startsAt <= now) {
            state = 'due'; when = 'Now';
          } else {
            // The heading has already given the visit's own start time, so a
            // line beginning at that same minute has nothing to add. A line
            // starting later in the visit is exactly what needs saying.
            when = s.startsAt === a.startsAt ? '' : at;
          }

          return {
            svc: s,
            mapped: mapped,
            requested: requested,
            canMark: !!booked && !fromBook,
            fromBook: fromBook,
            starTitle: fromBook
              ? 'Booked as a request' + (booked ? ' for ' + booked.name : '') + ' in Mangomint'
              : booked
              ? (requested
                ? 'A client request for ' + booked.name + ' — click to unmark it'
                : 'Click if the client asked for ' + booked.name + ' by name')
              : '',
            letter: mapped ? mapped.abbrev : '?',
            color: mapped ? mapped.color : null,
            turn: turn,
            done: done,
            state: state,
            when: when,
            tech: tech,
            flag: done ? '✓' : '●',
            title: (turn
              ? (done ? 'Completed by ' + tech : 'On the board with ' + tech)
              : host
                ? 'Added at the chair — part of ' + host.staffName + '’s ' + host.serviceName + ' turn'
              : over
                ? 'Checked out — nothing to assign'
              : state === 'late'
                ? 'Booked for ' + at + ' and not on the board — assign a turn'
                : state === 'due'
                  ? 'Due now — assign a turn'
                  : 'Booked for ' + at + ' — assign a turn') +
              (requested ? ' — client request' : '')
          };
        })
      };
    }

    /* One list, in the order the day happens.
     *
     * There were two tabs here, Upcoming and In progress, and they asked the
     * desk to know which side of "now" a booking was on before it could go
     * looking for it. Worse, they answered that about the whole visit: a
     * client whose first service was under way took her later, untouched
     * services into In progress with her, where they read as work already
     * being done — and they were nowhere to be found under Upcoming, which is
     * where somebody looking for the next thing to hand out would look.
     *
     * Everything is here now, in time order, and each line says for itself
     * where it is. Cancelled visits stay in place too, struck through, rather
     * than being filed somewhere that has to be gone to.
     */
    var rows = Vue.computed(function () {
      var now = store.minuteNow.value;   // minute resolution: Now and Late
      return visible.value
        .slice()
        .sort(function (x, y) { return x.appt.startsAt - y.appt.startsAt; })
        .map(function (r) { return decorate(r, now); });
    });

    // What is still to hand out, counted in LINES rather than visits — a visit
    // is not one piece of work, which is the whole reason the tabs are gone.
    // It replaces the counts the tabs carried.
    var toGive = Vue.computed(function () {
      var n = 0;
      store.appts.rows.forEach(function (a) {
        if (a.cancelled) return;
        a.services.forEach(function (s) {
          if (!store.turnForApptService(s.id)) n += 1;
        });
      });
      return n;
    });

    /* What the tab has to carry while the pane is shut.
     *
     * Shut, the tab is all that is left of the pane, and nothing else on the
     * board says a conflict exists — the list that says so is inside the very
     * thing that is closed. So the count comes out onto the edge in red. It
     * follows the same rule the list does and shows nothing when there is
     * nothing wrong; a mark that is always there is a mark nobody reads.
     *
     * Signed out the list does not draw at all, so neither does this: a badge
     * that opens onto a sign-in form is a promise the pane cannot keep.
     */
    var alerts = Vue.computed(function () {
      return store.auth.user ? store.bookConflicts.value.count : 0;
    });

    var tabTitle = Vue.computed(function () {
      if (store.appts.open) return 'Hide appointments';
      if (!alerts.value) return 'Show appointments';
      return 'Show appointments — ' + alerts.value + ' potential ' +
        (alerts.value === 1 ? 'conflict' : 'conflicts');
    });

    // Right-click says whether the client asked for the technician by name —
    // only where the desk may say so (`store.deskMarksRequests`). With a book
    // connected that comes from the book, and the browser's own menu is left
    // alone.
    function onServiceContext(evt, appt, svc) {
      if (!store.deskMarksRequests.value) return;
      evt.preventDefault();
      store.openApptMenu(appt, svc, evt.clientX, evt.clientY);
    }

    // The star is inside the service row, and the row assigns a turn when it
    // is clicked and drags when it is pressed. Both have to be held off here
    // or marking a request would open the assign sheet on top of it.
    function onStarClick(evt, appt, svc) {
      evt.stopPropagation();
      store.toggleApptRequest(appt, svc);
    }

    function onServiceClick(appt, svc) {
      if (drag.suppressed()) return;
      var existing = store.turnForApptService(svc.id);
      if (existing) { store.openCellMenu(existing.id); return; }
      if (!store.presentStaff.value.length) {
        store.toast('Nobody is on the board yet.', 'warn');
        return;
      }
      store.openAssign(appt, svc);
    }

    /* ---- drag a booked service out onto the board ---- */

    // Horizontally, because this pane scrolls up and down: a vertical drag has
    // to stay a scroll. The shared controller does the rest, so a card dragged
    // from here lands exactly as one dragged from Due now.
    var drag = apptDrag;

    function onSvcPointerDown(evt, appt, svc) {
      var mapped = store.trackerServiceForBooked(svc);
      drag.start(evt, {
        axis: 'x',
        serviceId: mapped ? mapped.id : null,
        staffIds: store.allowedStaffForBooked(svc),
        startsAt: svc.startsAt,
        durationMinutes: svc.durationMinutes,
        label: appt.clientName + ' · ' + svc.name,
        link: {
          appointmentId: appt.id, appointmentServiceId: svc.id,
          sourceServiceId: svc.serviceId, clientId: appt.clientId,
          clientName: appt.clientName, sourceServiceName: svc.name
        }
      });
    }

    Vue.onBeforeUnmount(drag.cancel);

    /* ---- writing up what already finished ---- */

    // Asked for before it happens: it puts several squares on the board at
    // once, and taking them off again is one square at a time.
    var confirmFill = Vue.ref(false);

    var fill = store.backfillCandidates;

    var fillLabel = Vue.computed(function () {
      // Add-ons go onto their squares rather than becoming turns, but they are
      // still finished work the press puts on the board.
      var n = fill.value.ready.length + fill.value.addOns.length;
      return n === 1 ? 'Write up 1 finished service' : 'Write up ' + n + ' finished services';
    });

    // What it will not guess at, said before the click rather than after.
    var fillNote = Vue.computed(function () {
      var f = fill.value;
      var bits = [];
      if (f.unmapped) bits.push(f.unmapped + ' unmapped');
      if (f.noTech) bits.push(f.noTech + ' with no technician');
      if (f.offBoard.length) bits.push(f.offBoard.join(', ') + ' not on the board');
      var lead = f.addOns.length
        ? (f.addOns.length === 1 ? '1 was added at the chair and goes onto its square. '
          : f.addOns.length + ' were added at the chair and go onto their squares. ')
        : '';
      return lead + (bits.length ? 'Leaving ' + bits.join(', ') + '.' : '');
    });

    function doFill() {
      store.backfillCompleted();
      confirmFill.value = false;
    }

    return {
      appts: store.appts,
      ui: store.ui,
      // Whether its star can be clicked — see `store.deskMarksRequests`.
      deskMarks: store.deskMarksRequests,
      auth: store.auth,
      rows: rows,
      toGive: toGive,
      alerts: alerts,
      tabTitle: tabTitle,
      hiddenCancelled: hiddenCancelled,
      onServiceClick: onServiceClick,
      onStarClick: onStarClick,
      onSvcPointerDown: onSvcPointerDown,
      onServiceContext: onServiceContext,
      toggle: store.toggleApptPane,
      reload: store.loadAppointments,
      formatClock: store.formatClock,
      setUnassignedOnly: store.setApptUnassignedOnly,
      setHideCancelled: store.setApptHideCancelled,
      fill: fill,
      fillLabel: fillLabel,
      fillNote: fillNote,
      confirmFill: confirmFill,
      doFill: doFill,
      closed: store.dayClosed
    };
  },
  template: [
    '<aside class="appt-pane" :class="{ \'is-closed\': !appts.open }">',
    '  <button type="button" class="appt-pane__tab" @click="toggle"',
    '    :aria-expanded="appts.open ? \'true\' : \'false\'"',
    /* The label is a caret, a bare number and a sideways word, which read out
       in that order says nothing. The sentence the badge stands for is the
       button's name instead, and the number itself is left to the eye. */
    '    :aria-label="tabTitle" :title="tabTitle">',
    '    <span class="appt-pane__tab-icon">{{ appts.open ? "\\u203A" : "\\u2039" }}</span>',
    /* Only while it is shut: open, the list itself is right there saying it,
       and the same count twice over reads as two different counts. */
    '    <span v-if="!appts.open && alerts" class="appt-pane__tab-alert"',
    '      aria-hidden="true">{{ alerts }}</span>',
    '    <span class="appt-pane__tab-label">Appointments</span>',
    '  </button>',
    '',
    '  <div v-if="appts.open" class="appt-pane__body">',
    '    <div class="appt-pane__head">',
    /* The count the tabs used to carry, in the one place left that is about
       the list as a whole — and in lines, because a line is what gets a
       square. */
    '      <h2>Today&rsquo;s book',
    '        <span v-if="auth.user && toGive" class="appt-pane__n"',
    '          :title="toGive + \' booked \' + (toGive === 1 ? \'service\' : \'services\') + \' still to hand out\'">',
    '          {{ toGive }}</span></h2>',
    '    </div>',
    '',
    /* ---- signed out ---- */
    '    <station-sign-in v-if="!auth.user && !auth.checking"',
    '      why="Today&rsquo;s book includes client names, so the station has to be signed in." />',
    '',
    /* ---- signed in ---- */
    '    <template v-else-if="auth.user">',
    /* Above the book because it is not part of it: a mismatch is about a
       square already up, a booking with nobody free for it is about one that
       is not, and a turn with no appointment is about neither. It draws
       nothing while there is nothing wrong. */
    '      <appt-conflicts />',
    '',
    /* The pane is a list of work still to hand out, so what is already on the
       board comes off it by default, and so does a visit that is not happening
       at all. Off, the whole book is here with the assigned lines struck
       through and the cancelled headings back in their places.
       Both say Hide and both mean it when on, side by side, so neither has to
       be read twice to tell which way round it works. */
    '      <div class="appt-filter">',
    '        <label class="switch switch--sm">',
    '          <input type="checkbox" :checked="ui.apptUnassignedOnly"',
    '            @change="setUnassignedOnly($event.target.checked)" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '          <span class="switch__label">Hide on board</span>',
    '        </label>',
    '        <label class="switch switch--sm">',
    '          <input type="checkbox" :checked="ui.apptHideCancelled"',
    '            @change="setHideCancelled($event.target.checked)" />',
    '          <span class="switch__track"><span class="switch__thumb"></span></span>',
    '          <span class="switch__label">Hide cancelled</span>',
    '        </label>',
    /* What the switch is currently keeping out, so the list never quietly
       stands for less of the day than it looks like. */
    '        <p v-if="hiddenCancelled" class="appt-filter__hidden">',
    '          {{ hiddenCancelled }} cancelled hidden',
    '          <button type="button" class="link-btn" @click="setHideCancelled(false)">Show</button>',
    '        </p>',
    '      </div>',
    '',
    /* ---- writing up services that finished while nobody was at the board ---- */
    '      <div v-if="(fill.ready.length || fill.addOns.length) && !closed" class="appt-fill">',
    '        <template v-if="!confirmFill">',
    '          <button type="button" class="btn btn--primary btn--sm btn--wide"',
    '            @click="confirmFill = true"',
    '            title="Put finished bookings on the board where they happened — started and stopped at the times the book has, in the right technician\'s column">',
    '            {{ fillLabel }}</button>',
    '        </template>',
    '        <template v-else>',
    '          <p class="appt-fill__ask">{{ fillLabel }} onto the board, at the times the book has for them?',
    '            <span v-if="fillNote" class="appt-fill__note">{{ fillNote }}</span></p>',
    '          <div class="appt-fill__row">',
    '            <button type="button" class="btn btn--primary btn--sm" @click="doFill">Write them up</button>',
    '            <button type="button" class="btn btn--quiet btn--sm" @click="confirmFill = false">Cancel</button>',
    '          </div>',
    '        </template>',
    '      </div>',
    '',
    '      <p v-if="appts.error" class="warn-line">{{ appts.error }}</p>',
    '      <p v-else-if="appts.loading && !appts.rows.length" class="appt-empty">Loading…</p>',
    /* With the filter on, an empty list means the board has everything —
       which is the good outcome and should not read as an empty diary. */
    '      <p v-else-if="!rows.length" class="appt-empty">',
    '        <template v-if="hiddenCancelled && ui.apptUnassignedOnly">',
    '          Everything left today is on the board or cancelled.',
    '          <button type="button" class="link-btn" @click="setUnassignedOnly(false)">Show all</button>',
    '        </template>',
    '        <template v-else-if="hiddenCancelled">Everything booked today was cancelled.</template>',
    '        <template v-else-if="ui.apptUnassignedOnly">',
    '          Everything booked today is on the board.',
    '          <button type="button" class="link-btn" @click="setUnassignedOnly(false)">Show all</button>',
    '        </template>',
    '        <template v-else>Nothing booked today.</template></p>',
    '',
    '      <div class="appt-group">',
    '        <article v-for="r in rows" :key="r.appt.id" class="appt"',
    '          :class="{ \'appt--cancelled\': r.appt.cancelled }">',
    '          <div class="appt__top">',
    '            <span class="appt__time">{{ formatClock(r.appt.startsAt) }}</span>',
    '            <span class="appt__client">{{ r.appt.clientName }}<client-note :client-id="r.appt.clientId" /></span>',
    '            <span v-if="r.appt.isFirst" class="appt__new" title="First visit">NEW</span>',
    '          </div>',
    '          <ul class="appt__services">',
    '            <li v-for="line in r.services" :key="line.svc.id" class="appt__svc"',
    '              :class="{ \'is-assigned\': line.turn, \'is-done\': line.done,',
    '                        \'is-late\': line.state === \'late\' }"',
    '              @pointerdown="onSvcPointerDown($event, r.appt, line.svc)"',
    '              @contextmenu="onServiceContext($event, r.appt, line.svc)"',
    '              @click="onServiceClick(r.appt, line.svc)"',
    '              :title="line.title">',
    '              <span class="appt__svc-letter"',
    '                :class="{ \'is-unmapped\': !line.mapped }"',
    '                :style="line.color ? { background: line.color } : null">',
    '                {{ line.letter }}',
    '              </span>',
    /* Once it is on the board the square is the record and carries its own
       star; the mark is only editable while the booking is still just a
       booking, so the name keeps its star on assigned lines and gives it up
       on the ones that have a live star beside the technician instead. */
    '              <span class="appt__svc-name">{{ line.svc.name }}',
    '                <span v-if="line.requested && line.turn" class="appt__svc-star"',
    '                  title="Client request">&#9733;</span></span>',
    '              <span v-if="line.turn" class="appt__svc-flag"',
    '                :class="line.done ? \'is-done\' : \'is-live\'">',
    '                {{ line.flag }} {{ line.tech }}</span>',
    '              <template v-else>',
    /* Left of the name it is a request FOR, which is the thing being said. */
    '                <button v-if="deskMarks && line.canMark" type="button"',
    '                  class="star-btn" :class="{ \'is-on\': line.requested }"',
    '                  :title="line.starTitle" :aria-label="line.starTitle"',
    '                  :aria-pressed="line.requested ? \'true\' : \'false\'"',
    '                  @pointerdown.stop @click="onStarClick($event, r.appt, line.svc)">',
    '                  {{ line.requested ? "\\u2605" : "\\u2606" }}</button>',
    '                <span v-else-if="ui.requestsOn && line.fromBook" class="star-btn is-on is-booked"',
    '                  :title="line.starTitle" :aria-label="line.starTitle">&#9733;</span>',
    '                <span v-if="line.svc.techs.length" class="appt__svc-tech">{{ line.svc.techs.join(", ") }}</span>',
    '                <span v-if="line.svc.durationMinutes" class="appt__svc-dur">{{ line.svc.durationMinutes }}m</span>',
    /* Where this line is on its own clock, in the same right-hand column the
       assigned lines put their technician in. It is what the tabs used to say
       about the whole visit, said about the one service it is true of. */
    '                <span v-if="line.when" class="appt__svc-at" :class="\'is-\' + line.state">{{ line.when }}</span>',
    '              </template>',
    '            </li>',
    '          </ul>',
    '          <p v-if="r.appt.notes" class="appt__notes">{{ r.appt.notes }}</p>',
    '        </article>',
    '      </div>',
    '',
    '      <p class="appt-pane__foot">',
    '        <span v-if="appts.lastSync">Live &middot; updated {{ formatClock(appts.lastSync) }}</span>',
    '        <button type="button" class="link-btn" @click="reload">Refresh</button>',
    '      </p>',
    '    </template>',
    '  </div>',
    '</aside>'
  ].join('\n')
};
